/**
 * CommentThread Component
 *
 * Displays comment thread for a feature request with ability to add new comments.
 *
 * @module components/feedback/CommentThread
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Person as UserIcon,
  Code as DevIcon,
  AdminPanelSettings as AdminIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/AuthContext';
import { FeatureRequestService } from '../../services/featureRequestService';
import { RequestComment } from '../../types/featureRequest';
import { formatDistanceToNow } from 'date-fns';

interface CommentThreadProps {
  requestId: string;
  isAdmin?: boolean;
}

/**
 * CommentThread - Display and add comments
 */
const CommentThread: React.FC<CommentThreadProps> = ({ requestId, isAdmin = false }) => {
  const user = useUser();

  // State
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');

  /**
   * Fetch comments
   */
  useEffect(() => {
    fetchComments();
  }, [requestId, isAdmin]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Include private comments if user is admin
      const fetchedComments = await FeatureRequestService.getComments(requestId, isAdmin);
      setComments(fetchedComments);
    } catch (err: any) {
      console.error('Error fetching comments:', err);
      setError(err.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle submit comment
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim()) {
      return;
    }

    if (!user) {
      setError('You must be logged in to comment');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const authorRole = isAdmin ? 'developer' : 'user';

      await FeatureRequestService.addComment(
        requestId,
        newComment,
        user.uid,
        user.displayName || user.email || 'Anonymous',
        authorRole,
        false // Not private
      );

      // Clear input
      setNewComment('');

      // Refresh comments
      await fetchComments();
    } catch (err: any) {
      console.error('Error adding comment:', err);
      setError(err.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Get role icon
   */
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminIcon fontSize="small" />;
      case 'developer':
        return <DevIcon fontSize="small" />;
      default:
        return <UserIcon fontSize="small" />;
    }
  };

  /**
   * Get role chip
   */
  const getRoleChip = (role: string) => {
    const config: Record<string, { label: string; color: any }> = {
      admin: { label: 'Admin', color: 'error' },
      developer: { label: 'Developer', color: 'primary' },
      user: { label: 'User', color: 'default' },
    };

    const roleConfig = config[role] || config.user;

    return (
      <Chip
        icon={getRoleIcon(role)}
        label={roleConfig.label}
        size="small"
        color={roleConfig.color}
        variant="outlined"
      />
    );
  };

  /**
   * Format date
   */
  const formatDate = (dateString: string): string => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Comments {comments.length > 0 && `(${comments.length})`}
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Comments List */}
      {!loading && (
        <Stack spacing={2} mb={3}>
          {comments.length === 0 ? (
            <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No comments yet. Be the first to comment!
              </Typography>
            </Paper>
          ) : (
            comments.map((comment) => (
              <Paper key={comment.id} elevation={1} sx={{ p: 2 }}>
                {/* Private indicator */}
                {comment.isPrivate && isAdmin && (
                  <Chip
                    label="Private (Admin Only)"
                    size="small"
                    color="warning"
                    sx={{ mb: 1 }}
                  />
                )}

                {/* Comment Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      {comment.authorName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">{comment.authorName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(comment.createdAt)}
                        {comment.isEdited && ' (edited)'}
                      </Typography>
                    </Box>
                  </Box>
                  {getRoleChip(comment.authorRole)}
                </Box>

                {/* Comment Content */}
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    pl: 5,
                  }}
                >
                  {comment.content}
                </Typography>
              </Paper>
            ))
          )}
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Add Comment Form */}
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Add a Comment
        </Typography>

        {user ? (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add your comment or ask for clarification..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              disabled={submitting}
              sx={{ mb: 2 }}
            />
            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                disabled={submitting || !newComment.trim()}
              >
                {submitting ? 'Posting...' : 'Post Comment'}
              </Button>
            </Box>
          </form>
        ) : (
          <Alert severity="info">
            Please log in to add comments
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default CommentThread;
